import { useEffect } from 'react';

interface IClickAwayProps {
	ref: any;
	open: boolean;
	onClickAway?: (event: MouseEvent) => void;
	onClickInside?: (event: MouseEvent) => void;
	onEscape?: (event: KeyboardEvent) => void;
	onTab?: (event: KeyboardEvent) => void;
	onShiftTab?: (event: KeyboardEvent) => void;
	onMousedownClickAway?: (event: MouseEvent) => void;
	onMouseupClickAway?: (event: MouseEvent) => void;
	onMousedownClickInside?: (event: MouseEvent) => void;
	exemptClassnames?: string[];
}
/**
 *
 * This hook listens for clicks outside of a given ref and calls the appropriate callback.
 *
 * ***It does not support touch screens.***
 */
const useClickAwayListener = ({
	ref,
	onClickAway,
	onClickInside,
	onMousedownClickAway,
	onMouseupClickAway,
	onMousedownClickInside,
	onEscape,
	onTab,
	onShiftTab,
	open,
	exemptClassnames = [],
}: IClickAwayProps): void => {

	// Helper function to check if an element or its parents have an exempt classname
	const hasExemptClassname = (element: EventTarget[] | null): boolean => {
		let doCancelClick = false;
		if (!element) return doCancelClick;
		for (const el of element) {
			if (el instanceof Element) {
				const currentElementClassList = Array.from(el?.classList || []);
				if (exemptClassnames.some(className => currentElementClassList.includes(className))) {
					doCancelClick = true;
				}
			}
		}
		return doCancelClick;
	}

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (!open) return;
			if (!ref?.current?.contains?.(event.target)) {
				onClickAway?.(event);
			} else {
				onClickInside?.(event);
			}
		};

		const handleMouseDownClickOutside = (event: MouseEvent) => {
			if (!open) return;
			
			// Skip if the clicked element has an exempt classname
			if (hasExemptClassname(event.composedPath())) {
				return;
			}
			
			if (!ref?.current?.contains?.(event.target)) {
				onMousedownClickAway?.(event);
				onMouseupClickAway?.(event);
			} else {
				onMousedownClickInside?.(event);
			}
		};

		const handleKeypress = (event: KeyboardEvent) => {
			// on escape
			if (open && event.key === 'Escape') {
				event.preventDefault();
				onEscape?.(event);
			}

			// on tab
			if (open && event.key === 'Tab' && !event.shiftKey) {
				onTab?.(event);
			}

			// on shift tab
			if (open && event.key === 'Tab' && event.shiftKey) {
				onShiftTab?.(event);
			}
		};

		document.addEventListener('click', handleClickOutside);
		if (onMousedownClickAway) {
			document.addEventListener('mousedown', handleMouseDownClickOutside);
		}
		if (onMouseupClickAway) {
			document.addEventListener('mouseup', handleMouseDownClickOutside);
		}
		document.addEventListener('keydown', handleKeypress);
		return () => {
			document.removeEventListener('click', handleClickOutside);
			document.removeEventListener('mousedown', handleMouseDownClickOutside);
			document.removeEventListener('mouseup', handleMouseDownClickOutside);
			document.removeEventListener('keydown', handleKeypress);
		};
	}, [ref, onClickAway, onClickInside, onEscape, open, onMousedownClickAway, onMousedownClickInside, onTab, onShiftTab, onMouseupClickAway, exemptClassnames]);
};

export default useClickAwayListener;
